/**
 * The recycle bin (סל מחזור) — the single source of truth for "is this task
 * deleted, how long has it got left, and is it due to be purged?".
 *
 * Deleting a task never removes a row: it stamps `deletedAt`, which takes the
 * task off the board and puts it in the bin. A task waits there for
 * {@link RECYCLE_BIN_RETENTION_DAYS} days, restorable at any moment, and is
 * then removed for good — either by the automatic sweep the board runs on load
 * (`purgeExpiredTasks()` in `src/db/task-store.ts`) or by the manager emptying
 * the bin by hand.
 *
 * As with the other rule modules, the arithmetic lives here rather than inside
 * the store or the screens, so the countdown a card renders and the moment the
 * sweep acts on are the same answer.
 */
import { RECYCLE_BIN_RETENTION_DAYS, RECYCLE_BIN_RETENTION_MS } from '../types/task';
import type { Task } from '../types/task';

const DAY_MS = 24 * 60 * 60 * 1000;

/** True when the task is in the recycle bin rather than on the board. */
export function isDeleted(task: Pick<Task, 'deletedAt'>): boolean {
  return Boolean(task.deletedAt);
}

/** The board — every task that is not in the bin. */
export function activeTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter((task) => !isDeleted(task));
}

/** The bin itself, most recently deleted first. */
export function deletedTasks(tasks: readonly Task[]): Task[] {
  return tasks
    .filter(isDeleted)
    .sort((a, b) => String(b.deletedAt).localeCompare(String(a.deletedAt)));
}

/**
 * The moment a deleted task is removed for good, or `null` for a task that is
 * not in the bin (or carries an unreadable stamp).
 */
export function purgeDueAt(task: Pick<Task, 'deletedAt'>): number | null {
  if (!task.deletedAt) return null;
  const deleted = Date.parse(task.deletedAt);
  if (Number.isNaN(deleted)) return null;
  return deleted + RECYCLE_BIN_RETENTION_MS;
}

/** True once the 30 days have run out and the row may be removed. */
export function isPurgeDue(task: Pick<Task, 'deletedAt'>, now: number = Date.now()): boolean {
  const due = purgeDueAt(task);
  return due !== null && due <= now;
}

/**
 * Whole days left before the automatic purge, rounded up and never negative.
 * A task with less than a day to go reads `0` — "היום".
 */
export function daysLeftInBin(task: Pick<Task, 'deletedAt'>, now: number = Date.now()): number {
  const due = purgeDueAt(task);
  if (due === null) return RECYCLE_BIN_RETENTION_DAYS;
  return Math.max(0, Math.ceil((due - now) / DAY_MS));
}

/** The countdown badge the bin renders: "נמחקת לצמיתות בעוד 30 ימים". */
export function formatBinCountdown(
  task: Pick<Task, 'deletedAt'>,
  now: number = Date.now(),
): string {
  if (isPurgeDue(task, now)) return 'תימחק לצמיתות בסבב הניקוי הבא';
  const days = daysLeftInBin(task, now);
  if (days === 0) return 'תימחק לצמיתות היום';
  if (days === 1) return 'תימחק לצמיתות מחר';
  return `תימחק לצמיתות בעוד ${days} ימים`;
}

/** Every task whose retention has run out — what the sweep removes. */
export function expiredTasks(tasks: readonly Task[], now: number = Date.now()): Task[] {
  return tasks.filter((task) => isDeleted(task) && isPurgeDue(task, now));
}
